"use client";

import { Alert, Anchor, Box, Stack as MantineStack } from "@mantine/core";
import { FiBell, FiCheck, FiExternalLink, FiInfo } from "react-icons/fi";

import {
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Text,
} from "@/components/ui/elements";

import { PRIVACY_POLICY_URL } from "./_function/privacyPolicyConstants";

type PrivacyPolicyRevisionNoticeModalProps = {
  opened: boolean;
  onConfirm: () => void;
};

const revisionIntro =
  "運転免許証を資格提出書類として扱う方式へ変更したことに伴い、プライバシーポリシーを改訂しました。";

const revisionSections = [
  {
    title: "2. 個人情報の収集",
    description:
      "運転免許証等を資格書類として提出する場合の取得方法を追記しました。",
  },
  {
    title: "3. 個人情報の利用目的",
    description:
      "資格書類（運転免許証を含む）の確認・審査、運転必須求人への応募可否・マッチング・勤務配属の判定を追記しました。",
  },
  {
    title: "4. 個人情報の第三者提供",
    description: "運転免許証の取扱い（収集する情報・利用目的）を追記しました。",
  },
] as const;

const summaryCardStyle = {
  borderRadius: "12px",
  padding: "14px 16px",
  backgroundColor: "var(--mantine-color-blue-0)",
  border: "1px solid var(--mantine-color-blue-2)",
} as const;

const sectionCardStyle = {
  borderRadius: "10px",
  padding: "12px 14px",
  backgroundColor: "var(--mantine-color-white)",
  border: "1px solid var(--mantine-color-gray-3)",
} as const;

const linkCardStyle = {
  borderRadius: "10px",
  padding: "12px 14px",
  backgroundColor: "var(--mantine-color-gray-0)",
  border: "1px solid var(--mantine-color-gray-3)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
} as const;

const confirmButtonWrapperStyle = {
  position: "sticky",
  bottom: 0,
  marginTop: "var(--mantine-spacing-lg)",
  paddingTop: "var(--mantine-spacing-lg)",
  backgroundColor: "var(--mantine-color-body)",
  borderTop: "1px solid var(--mantine-color-gray-3)",
  zIndex: 1000,
} as const;

export function PrivacyPolicyRevisionNoticeModal({
  opened,
  onConfirm,
}: PrivacyPolicyRevisionNoticeModalProps) {
  return (
    <Modal
      opened={opened}
      close={onConfirm}
      centered
      size="md"
      padding="xl"
      withCloseButton
      closeOnClickOutside={false}
      closeOnEscape={false}
      styles={{
        header: {
          padding: "8px 12px",
          minHeight: "auto",
        },
        body: {
          paddingTop: "var(--mantine-spacing-md)",
          paddingBottom: "var(--mantine-spacing-md)",
        },
      }}
    >
      <Stack gap="lg">
        <Box
          style={{
            background:
              "linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 50%, #ede7f6 100%)",
            borderRadius: "16px",
            padding: "20px 16px",
            border: "1px solid var(--mantine-color-blue-2)",
          }}
        >
          <Stack gap="sm" align="center">
            <Badge
              size="md"
              variant="filled"
              color="blue"
              radius="xl"
              fw={700}
              leftSection={<FiBell size={15} aria-hidden />}
              styles={{
                root: {
                  paddingLeft: 12,
                  paddingRight: 12,
                  height: 28,
                  textTransform: "none",
                  boxShadow: "0 2px 8px rgba(25, 118, 210, 0.25)",
                },
              }}
            >
              プライバシーポリシー改訂のお知らせ
            </Badge>
            <Text
              fw={800}
              ta="center"
              style={{
                fontSize: "22px",
                color: "var(--mantine-color-blue-9)",
                lineHeight: 1.45,
                letterSpacing: "-0.02em",
              }}
            >
              プライバシーポリシーを
              <br />
              改訂しました
            </Text>
          </Stack>
        </Box>

        <Alert
          icon={<FiInfo size={16} aria-hidden />}
          color="blue"
          variant="light"
          radius="md"
          styles={{
            message: {
              fontSize: 13,
              lineHeight: 1.7,
              color: "var(--mantine-color-blue-8)",
            },
          }}
        >
          {revisionIntro}
        </Alert>

        <Stack gap="xs">
          <Text size="sm" fw={700} c="dark.6">
            主な改訂内容
          </Text>
          <Box style={summaryCardStyle}>
            <MantineStack gap="sm">
              {revisionSections.map((section) => (
                <Box key={section.title} style={sectionCardStyle}>
                  <Text size="sm" fw={700} c="blue.8" mb={4}>
                    【{section.title}】
                  </Text>
                  <Text size="sm" lh={1.7} c="dark.6">
                    {section.description}
                  </Text>
                </Box>
              ))}
            </MantineStack>
          </Box>
        </Stack>

        <Box style={linkCardStyle}>
          <Anchor
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            fw={600}
            underline="never"
            c="blue.7"
          >
            <Group gap={6} wrap="nowrap" justify="center">
              <FiExternalLink size={15} aria-hidden />
              プライバシーポリシー全文を確認する
            </Group>
          </Anchor>
        </Box>
      </Stack>

      <Box style={confirmButtonWrapperStyle}>
        <Button
          variant="outline"
          size="lg"
          color="gray"
          fullWidth
          onClick={onConfirm}
          styles={{
            root: {
              height: 48,
              fontSize: 15,
              fontWeight: 600,
              color: "var(--mantine-color-dark-7)",
              borderColor: "var(--mantine-color-gray-4)",
            },
          }}
        >
          <Group gap="xs" justify="center">
            <FiCheck size={18} aria-hidden />
            確認しました
          </Group>
        </Button>
      </Box>
    </Modal>
  );
}
